// frontend/src/components/Dashboard.jsx - Mobile-Optimized with Performance & Animations
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import apiService from '../services/api';
import ModuleView from './ModuleView';
import ChapterView from './ChapterView';
import AdminPanel from '../components/admin/AdminPanel';
import { 
  BookOpen, 
  Play, 
  CheckCircle, 
  Clock, 
  Award, 
  LogOut,
  Settings,
  User,
  BarChart3,
  Target,
  ChevronRight,
  Video,
  Loader,
  RefreshCw,
  AlertTriangle,
  Menu,
  X,
  TrendingUp,
  Zap,
  Star
} from 'lucide-react';

// Optimized Error Boundary Component
const ModulesErrorBoundary = ({ children, error, onRetry, onReload }) => {
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full text-center animate-fadeIn">
          <div className="text-red-500 mb-4">
            <AlertTriangle className="w-12 h-12 mx-auto animate-bounce" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error de Carga</h2>
          <p className="text-gray-600 mb-4 text-sm">{error}</p>
          <div className="space-y-3">
            <button 
              onClick={onRetry}
              className="w-full bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 transition-all duration-300 flex items-center justify-center space-x-2 hover:scale-105 transform text-base font-medium"
            >
              <RefreshCw className="w-4 h-4" />
              <span>Reintentar</span>
            </button>
            <button 
              onClick={onReload}
              className="w-full bg-gray-500 text-white px-4 py-3 rounded-lg hover:bg-gray-600 transition-all duration-300 hover:scale-105 transform text-base"
            >
              Recargar Página
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  return children;
};

const Dashboard = () => {
  const { user, logout, isAdmin, loading: authLoading, isAuthenticated } = useAuth();
  
  // State management
  const [currentView, setCurrentView] = useState('dashboard');
  const [selectedModule, setSelectedModule] = useState(null);
  const [selectedChapter, setSelectedChapter] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [durationCalculating, setDurationCalculating] = useState(false);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [animationReady, setAnimationReady] = useState(false);
  
  // Refs for performance
  const moduleRefs = useRef({});
  const observerRef = useRef(null);
  
  // User stats state
  const [userStats, setUserStats] = useState({
    totalModules: 0,
    completedModules: 0,
    totalChapters: 0,
    completedChapters: 0,
    totalWatchTime: 0,
    overallProgress: 0
  });

  // Optimized data fetching
  useEffect(() => {
    if (authLoading || !isAuthenticated) {
      console.log('⏳ Dashboard: Waiting for auth...');
      return;
    }
    
    fetchModulesWithStats();
  }, [authLoading, isAuthenticated]);

  // Animate elements after data loads
  useEffect(() => {
    if (modules.length > 0 && !loading) {
      setTimeout(() => setAnimationReady(true), 100);
    }
  }, [modules, loading]);

  // Intersection Observer for lazy loading animations
  useEffect(() => {
    if (!animationReady) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-slideUp');
          }
        });
      },
      { threshold: 0.1, rootMargin: '50px' }
    );

    const elements = document.querySelectorAll('.lazy-animate');
    elements.forEach((el) => {
      if (observerRef.current) {
        observerRef.current.observe(el);
      }
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [animationReady]);

  // Parse duration string to minutes (synchronous, no video loading)
  const parseDurationToMinutes = useCallback((durationString) => {
    if (!durationString) return 0;
    
    const duration = durationString.toLowerCase();
    let minutes = 0;
    
    // Parse hours
    const hoursMatch = duration.match(/(\d+)h/);
    if (hoursMatch) {
      minutes += parseInt(hoursMatch[1]) * 60;
    }
    
    // Parse minutes
    const minutesMatch = duration.match(/(\d+)\s*min/);
    if (minutesMatch) {
      minutes += parseInt(minutesMatch[1]);
    }
    
    // Parse seconds (only if no minutes specified)
    const secondsMatch = duration.match(/(\d+)\s*seg/);
    if (secondsMatch && !minutesMatch) {
      minutes += Math.ceil(parseInt(secondsMatch[1]) / 60);
    }
    
    return minutes;
  }, []);

  // Format minutes to readable duration string
  const formatDuration = useCallback((totalMinutes) => {
    if (totalMinutes >= 60) {
      const hours = Math.floor(totalMinutes / 60);
      const remainingMinutes = totalMinutes % 60;
      return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}min` : `${hours}h`;
    } else if (totalMinutes > 0) {
      return `${totalMinutes}min`;
    }
    return '0min';
  }, []);

  // Replace ONLY the calculateModuleDuration function:
  const calculateModuleDuration = useCallback(async (chapters) => {
    if (!chapters?.length) {
      return { formatted: 'Sin videos', minutes: 0, hasVideos: false };
    }

    const hasVideos = chapters.some(ch => ch.video_url);
    if (!hasVideos) {
      return { formatted: 'Sin videos', minutes: 0, hasVideos: false };
    }

    console.log(`🔍 Loading ${chapters.length} videos...`);

    // Simple: load one video at a time
    let totalSeconds = 0;
    
    for (let i = 0; i < chapters.length; i++) {
      const chapter = chapters[i];
      if (!chapter.video_url) continue;

      const seconds = await new Promise((resolve) => {
        const video = document.createElement('video');
        video.preload = 'metadata';
        video.muted = true;
        
        const timeout = setTimeout(() => resolve(0), 5000);
        
        video.onloadedmetadata = () => {
          clearTimeout(timeout);
          console.log(`  ✅ Chapter ${i + 1}: ${video.duration.toFixed(2)}s`);
          resolve(video.duration || 0);
        };
        
        video.onerror = () => {
          clearTimeout(timeout);
          resolve(0);
        };
        
        try {
          video.src = apiService.getVideoUrl(chapter.video_url);
        } catch {
          clearTimeout(timeout);
          resolve(0);
        }
      });
      
      totalSeconds += seconds;
    }
    
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = Math.floor(totalSeconds % 60);
    
    let formatted;
    if (minutes >= 60) {
      const h = Math.floor(minutes / 60);
      const m = minutes % 60;
      formatted = m > 0 ? `${h}h ${m}min` : `${h}h`;
    } else if (minutes > 0) {
      formatted = seconds > 0 ? `${minutes}min ${seconds}seg` : `${minutes}min`;
    } else {
      formatted = seconds > 0 ? `${seconds}seg` : '0min';
    }
    
    console.log(`📊 TOTAL: ${formatted}`);

    return { formatted, minutes, hasVideos: true };
  }, []);

  // Enhanced error handling and retry logic
  const fetchModulesWithStats = useCallback(async (isRetry = false) => {
    try {
      setLoading(true);
      setDurationCalculating(true); // Show "Calculating durations..." message
      setError(null);
      
      console.log('🔄 Fetching modules with stats...');
      
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found. Please log in again.');
      }

      apiService.setToken(token);
      
      const modulesData = await apiService.getModules();
      console.log('📊 Modules data received:', modulesData?.length || 0, 'modules');
      
      if (!modulesData || !Array.isArray(modulesData)) {
        throw new Error('Invalid modules data received from server');
      }
      
      // Process modules with REAL video duration calculation
      console.log('⚙️ Processing modules and calculating durations from actual videos...');
      
      const modulesWithProgress = await Promise.all(
        modulesData.map(async (module, moduleIndex) => {
          try {
            console.log(`\n📦 Module ${moduleIndex + 1}/${modulesData.length}: ${module.title}`);
            
            const moduleChapters = await apiService.getChapters(module.id);
            console.log(`  └─ Chapters loaded: ${moduleChapters?.length || 0}`);
            
            // Calculate duration from ACTUAL videos (async)
            const durationData = await calculateModuleDuration(moduleChapters || []);
            console.log(`  └─ Duration: ${durationData.formatted}`);
            
            const totalChapters = moduleChapters?.length || 0;
            const completedChapters = moduleChapters?.filter(ch => ch.completed)?.length || 0;
            const progressPercentage = totalChapters > 0 ? Math.round((completedChapters / totalChapters) * 100) : 0;
            
            const processedModule = {
              ...module,
              chapters: durationData.calculatedChapters || moduleChapters || [],
              total_chapters: totalChapters,
              completed_chapters: completedChapters,
              progress_percentage: progressPercentage,
              total_duration: durationData.formatted,
              total_minutes: durationData.minutes,
              has_videos: durationData.hasVideos,
              duration_calculated: true
            };
            
            console.log(`  ✅ Module complete: ${durationData.formatted}`);
            
            return processedModule;
          } catch (chapterError) {
            console.error(`❌ Error processing module ${module.id}:`, chapterError);
            return {
              ...module,
              chapters: [],
              total_chapters: 0,
              completed_chapters: 0,
              progress_percentage: 0,
              total_duration: 'Error',
              total_minutes: 0,
              has_videos: false,
              duration_calculated: false
            };
          }
        })
      );
      
      console.log('\n✅ All modules processed!');
      console.log('📊 Summary:', modulesWithProgress.map(m => ({
        title: m.title,
        duration: m.total_duration,
        chapters: m.total_chapters
      })));
      
      setModules(modulesWithProgress);
      calculateUserStats(modulesWithProgress);
      setRetryCount(0);
      
      console.log('✅ State updated successfully');
      
    } catch (error) {
      console.error('❌ Failed to fetch modules:', error);
      
      let errorMessage = 'Error al cargar los módulos. Por favor intenta de nuevo.';
      
      if (error.message.includes('Token is required') || 
          error.message.includes('401') || 
          error.message.includes('Authentication failed') ||
          error.message.includes('Unauthorized')) {
        errorMessage = 'Sesión expirada. Cerrando sesión...';
        
        setTimeout(() => {
          console.log('🚪 Auto-logout due to auth error');
          logout();
        }, 2000);
        
      } else if (error.message.includes('Network') || error.message.includes('fetch')) {
        errorMessage = 'Error de conexión. Verifica tu conexión a internet.';
      } else if (error.message.includes('No authentication token')) {
        errorMessage = 'No se encontró token de autenticación. Redirigiendo al login...';
        setTimeout(() => logout(), 1000);
      }
      
      setError(errorMessage);
      
      if (!isRetry && retryCount < 3) {
        console.log(`🔄 Auto-retry attempt ${retryCount + 1}/3 in 2 seconds...`);
        setTimeout(() => {
          setRetryCount(prev => prev + 1);
          fetchModulesWithStats(true);
        }, 2000);
      }
      
    } finally {
      setLoading(false);
      setDurationCalculating(false);
    }
  }, [calculateModuleDuration, logout, retryCount]);

  const calculateUserStats = useCallback((modulesData) => {
    const totalModules = modulesData.length;
    const completedModules = modulesData.filter(module => module.progress_percentage === 100).length;
    
    const totalChapters = modulesData.reduce((sum, module) => sum + (module.total_chapters || 0), 0);
    const completedChapters = modulesData.reduce((sum, module) => sum + (module.completed_chapters || 0), 0);
    
    const overallProgress = totalChapters > 0 ? Math.round((completedChapters / totalChapters) * 100) : 0;
    const totalWatchTime = modulesData.reduce((sum, module) => sum + (module.total_minutes || 0), 0);

    setUserStats({
      totalModules,
      completedModules,
      totalChapters,
      completedChapters,
      totalWatchTime,
      overallProgress
    });
  }, []);

  const formatWatchTime = useCallback((minutes) => {
    if (minutes < 60) {
      return `${minutes}min`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}min` : `${hours}h`;
  }, []);

  const handleNavigation = useCallback((view, data = {}) => {
    setCurrentView(view);
    
    if (view === 'module') {
      setSelectedModule(data.selectedModule);
      setSelectedChapter(null);
      setChapters([]);
    } else if (view === 'chapter') {
      setSelectedModule(data.selectedModule);
      setSelectedChapter(data.selectedChapter);
      setChapters(data.chapters || []);
    } else if (view === 'dashboard') {
      setSelectedModule(null);
      setSelectedChapter(null);
      setChapters([]);
      fetchModulesWithStats();
    } else if (view === 'admin') {
      setSelectedModule(null);
      setSelectedChapter(null);
      setChapters([]);
    }
  }, [fetchModulesWithStats]);

  const handleModuleClick = useCallback((module) => {
    const moduleElement = moduleRefs.current[module.id];
    if (moduleElement) {
      moduleElement.style.transform = 'scale(0.98)';
      setTimeout(() => {
        moduleElement.style.transform = 'scale(1)';
      }, 150);
    }
    
    setTimeout(() => {
      handleNavigation('module', { 
        selectedModule: module,
        chapters: module.chapters || []
      });
    }, 200);
  }, [handleNavigation]);

  const handleRetry = useCallback(() => {
    setError(null);
    setRetryCount(0);
    fetchModulesWithStats();
  }, [fetchModulesWithStats]);

  const handleReload = useCallback(() => {
    window.location.reload();
  }, []);

  // Memoized calculations
  const getOverallProgress = useMemo(() => {
    return userStats.overallProgress || 0;
  }, [userStats.overallProgress]);

  const getRecentModules = useMemo(() => {
    return modules
      .filter(module => module.progress_percentage > 0 && module.progress_percentage < 100)
      .slice(0, 3);
  }, [modules]);

  const getNextModule = useMemo(() => {
    return modules.find(module => module.progress_percentage === 0);
  }, [modules]);

  // FIXED: getFeaturedModule now ensures all duration data is available
  const getFeaturedModule = useMemo(() => {
    const featured = getNextModule || getRecentModules[0] || modules[0];
    
    // Ensure the featured module has all the necessary data with fallbacks
    if (!featured) return null;
    
    return {
      ...featured,
      total_duration: featured.total_duration || '0min',
      total_chapters: featured.total_chapters || 0,
      progress_percentage: featured.progress_percentage || 0,
      total_minutes: featured.total_minutes || 0,
      has_videos: featured.has_videos || false,
      duration_calculated: featured.duration_calculated || false,
      icon: featured.icon || '📚'
    };
  }, [getNextModule, getRecentModules, modules]);

  // Render different views
  if (currentView === 'admin' && isAdmin) {
    return (
      <AdminPanel 
        onBack={() => handleNavigation('dashboard')}
      />
    );
  }

  if (currentView === 'module' && selectedModule) {
    return (
      <ModuleView
        selectedModule={selectedModule}
        onNavigate={handleNavigation}
        onBack={() => handleNavigation('dashboard')}
      />
    );
  }

  if (currentView === 'chapter' && selectedChapter && selectedModule) {
    return (
      <ChapterView
        selectedModule={selectedModule}
        selectedChapter={selectedChapter}
        chapters={chapters}
        allModules={modules}
        onBack={() => handleNavigation('module', { selectedModule })}
        onNavigate={handleNavigation}
      />
    );
  }

  // Enhanced mobile-first animation styles
  const animationStyles = `
    .animate-fadeIn { 
      animation: fadeIn 0.6s ease-out forwards; 
      opacity: 0;
    }
    .animate-slideUp { 
      animation: slideUp 0.8s ease-out forwards; 
      opacity: 0;
    }
    .animate-scaleIn { 
      animation: scaleIn 0.4s ease-out forwards; 
      opacity: 0;
    }
    .animate-bounce-gentle {
      animation: bounceGentle 2s infinite;
    }
    .stagger-1 { animation-delay: 0.1s; }
    .stagger-2 { animation-delay: 0.2s; }
    .stagger-3 { animation-delay: 0.3s; }
    .stagger-4 { animation-delay: 0.4s; }
    
    @keyframes fadeIn {
      from { opacity: 0; transform: translateY(20px); }
      to { opacity: 1; transform: translateY(0); }
    }
    @keyframes slideUp {
      from { opacity: 0; transform: translateY(30px); }
      to { opacity: 1; transform: translateY(0); }
    }
    @keyframes scaleIn {
      from { opacity: 0; transform: scale(0.95); }
      to { opacity: 1; transform: scale(1); }
    }
    @keyframes bounceGentle {
      0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
      40% { transform: translateY(-4px); }
      60% { transform: translateY(-2px); }
    }
  `;

  // Main Dashboard View
  return (
    <ModulesErrorBoundary 
      error={error} 
      onRetry={handleRetry} 
      onReload={handleReload}
    >
      <style>{animationStyles}</style>
      {loading ? (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
          <div className="text-center animate-fadeIn max-w-sm w-full">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="mt-4 text-gray-600 text-base">Cargando módulos...</p>
            {retryCount > 0 && (
              <p className="mt-3 text-sm text-orange-600">Reintento {retryCount}/3...</p>
            )}
          </div>
        </div>
      ) : (
        <div className="min-h-screen bg-gray-50">
          <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">

            {/* FIXED: Featured Module Section with proper null checking and duration display */}
            {getFeaturedModule && (
              <div className={`mb-6 sm:mb-8 ${animationReady ? 'animate-fadeIn' : 'opacity-0'}`}>
                <div className="bg-blue-600 rounded-lg p-1 shadow-lg">
                  <div className="bg-white rounded-lg p-4 sm:p-6 lg:p-8">
                    <div className="flex flex-col space-y-6 lg:flex-row lg:items-center lg:space-y-0 lg:space-x-8">
                      <div className="flex-1 text-center lg:text-left">
                        <div className="inline-flex items-center space-x-2 bg-yellow-100 text-yellow-800 px-3 py-1.5 rounded-full text-xs sm:text-sm font-medium mb-4">
                          <Star className="w-3 h-3 sm:w-4 sm:h-4" />
                          <span>MÓDULO DESTACADO</span>
                        </div>
                        <h2 className="text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-bold text-gray-900 mb-3 leading-tight">
                          {getFeaturedModule.title}
                        </h2>
                        <p className="text-gray-600 mb-6 leading-relaxed text-sm sm:text-base">
                          {getFeaturedModule.description}
                        </p>
                        
                        <div className="grid grid-cols-3 gap-3 sm:gap-4 mb-6">
                          <div className="text-center">
                            <div className="bg-blue-100 w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 rounded-lg flex items-center justify-center mx-auto mb-2">
                              <BookOpen className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-blue-600" />
                            </div>
                            <p className="text-base sm:text-lg lg:text-xl font-bold text-gray-900">{getFeaturedModule.total_chapters}</p>
                            <p className="text-xs sm:text-sm text-gray-600">Capítulos</p>
                          </div>
                          <div className="text-center">
                            <div className="bg-green-100 w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 rounded-lg flex items-center justify-center mx-auto mb-2">
                              <Clock className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-green-600" />
                            </div>
                            <p className="text-base sm:text-lg lg:text-xl font-bold text-gray-900">
                              {getFeaturedModule.total_duration}
                            </p>
                            <p className="text-xs sm:text-sm text-gray-600">Duración</p>
                          </div>
                          <div className="text-center">
                            <div className="bg-purple-100 w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 rounded-lg flex items-center justify-center mx-auto mb-2">
                              <Target className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-purple-600" />
                            </div>
                            <p className="text-base sm:text-lg lg:text-xl font-bold text-gray-900">{getFeaturedModule.progress_percentage}%</p>
                            <p className="text-xs sm:text-sm text-gray-600">Completado</p>
                          </div>
                        </div>

                        <button
                          onClick={() => handleModuleClick(getFeaturedModule)}
                          className="w-full sm:w-auto bg-blue-600 text-white px-6 py-3.5 rounded-lg text-base sm:text-lg font-semibold hover:bg-blue-700 transition-all duration-300 transform hover:scale-105 hover:shadow-lg inline-flex items-center justify-center space-x-2"
                        >
                          <Play className="w-5 h-5" />
                          <span>
                            {getFeaturedModule.progress_percentage === 0 ? 'Comenzar Ahora' : 
                             getFeaturedModule.progress_percentage < 100 ? 'Continuar Aprendiendo' : 
                             'Revisar Contenido'}
                          </span>
                        </button>
                      </div>

                      <div className="flex-shrink-0 flex justify-center lg:justify-end">
                        <div className="relative">
                          <div className="bg-blue-100 w-20 h-20 sm:w-24 sm:h-24 lg:w-32 lg:h-32 rounded-2xl flex items-center justify-center shadow-lg transition-transform hover:scale-110 animate-bounce-gentle">
                            <span className="text-2xl sm:text-3xl lg:text-4xl">{getFeaturedModule.icon}</span>
                          </div>
                          {getFeaturedModule.progress_percentage > 0 && (
                            <div className="absolute -bottom-1 -right-1 sm:-bottom-2 sm:-right-2 bg-blue-600 text-white px-2 py-1 rounded-full text-xs font-bold">
                              {getFeaturedModule.progress_percentage}%
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className={`bg-white rounded-lg shadow-sm border p-4 sm:p-6 mb-6 sm:mb-8 lazy-animate ${animationReady ? 'animate-fadeIn stagger-1' : 'opacity-0'}`}>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-6 gap-3 sm:gap-4">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Tu Progreso</h2>
                <div className="flex items-center space-x-2 self-start sm:self-center">
                  <BarChart3 className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                  <span className="text-lg sm:text-xl font-bold text-blue-900">{getOverallProgress}%</span>
                </div>
              </div>
              
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6 mb-4 sm:mb-6">
                <div className="flex items-center space-x-2 sm:space-x-3">
                  <div className="bg-blue-100 p-2 sm:p-3 rounded-lg flex-shrink-0">
                    <BookOpen className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-blue-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs sm:text-sm text-gray-600">Módulos</p>
                    <p className="text-base sm:text-lg lg:text-xl font-bold text-gray-900">
                      {userStats.completedModules}/{userStats.totalModules}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2 sm:space-x-3">
                  <div className="bg-green-100 p-2 sm:p-3 rounded-lg flex-shrink-0">
                    <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-green-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs sm:text-sm text-gray-600">Capítulos</p>
                    <p className="text-base sm:text-lg lg:text-xl font-bold text-gray-900">
                      {userStats.completedChapters}/{userStats.totalChapters}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2 sm:space-x-3">
                  <div className="bg-purple-100 p-2 sm:p-3 rounded-lg flex-shrink-0">
                    <Target className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-purple-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs sm:text-sm text-gray-600">Progreso</p>
                    <p className="text-base sm:text-lg lg:text-xl font-bold text-gray-900">{getOverallProgress}%</p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2 sm:space-x-3">
                  <div className="bg-orange-100 p-2 sm:p-3 rounded-lg flex-shrink-0">
                    <Clock className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-orange-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs sm:text-sm text-gray-600">Tiempo Total</p>
                    <p className="text-base sm:text-lg lg:text-xl font-bold text-gray-900">{formatWatchTime(userStats.totalWatchTime)}</p>
                  </div>
                </div>
              </div>
              
              <div className="w-full h-2 sm:h-3 bg-gray-200 rounded-full">
                <div
                  className="h-full bg-gradient-to-r from-blue-500 to-green-500 rounded-full transition-all duration-1000"
                  style={{ width: `${getOverallProgress}%` }}
                />
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Progreso general basado en videos y capítulos completados
              </p>
            </div>

            {(getRecentModules.length > 0 || getNextModule) && (
              <div className={`bg-white rounded-lg shadow-sm border p-4 sm:p-6 mb-6 sm:mb-8 lazy-animate ${animationReady ? 'animate-fadeIn stagger-2' : 'opacity-0'}`}>
                <div className="flex items-center space-x-3 mb-4">
                  <TrendingUp className="w-5 h-5 text-blue-600" />
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900">Acciones Rápidas</h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                  {getNextModule && (
                    <button
                      onClick={() => handleModuleClick(getNextModule)}
                      className="flex items-center space-x-3 p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition-all duration-300 text-left hover:scale-105 transform hover:shadow-md"
                    >
                      <Play className="w-5 h-5 text-blue-600 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-blue-900 text-sm sm:text-base">Comenzar Aprendizaje</p>
                        <p className="text-xs sm:text-sm text-blue-600 truncate">{getNextModule.title}</p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    </button>
                  )}
                  
                  {getRecentModules.length > 0 && (
                    <button
                      onClick={() => handleModuleClick(getRecentModules[0])}
                      className="flex items-center space-x-3 p-4 bg-green-50 rounded-lg hover:bg-green-100 transition-all duration-300 text-left hover:scale-105 transform hover:shadow-md"
                    >
                      <BookOpen className="w-5 h-5 text-green-600 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-green-900 text-sm sm:text-base">Continuar</p>
                        <p className="text-xs sm:text-sm text-green-600 truncate">{getRecentModules[0].title}</p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-green-600 flex-shrink-0" />
                    </button>
                  )}
                  
                  {userStats.completedModules > 0 && (
                    <button
                      onClick={() => {
                        const completedModule = modules.find(m => m.progress_percentage === 100);
                        if (completedModule) handleModuleClick(completedModule);
                      }}
                      className="flex items-center space-x-3 p-4 bg-purple-50 rounded-lg hover:bg-purple-100 transition-all duration-300 text-left hover:scale-105 transform hover:shadow-md"
                    >
                      <Award className="w-5 h-5 text-purple-600 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-purple-900 text-sm sm:text-base">Revisar Completados</p>
                        <p className="text-xs sm:text-sm text-purple-600">Repasar contenido</p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-purple-600 flex-shrink-0" />
                    </button>
                  )}
                </div>
              </div>
            )}

            <div className={`mb-6 lazy-animate ${animationReady ? 'animate-slideUp stagger-3' : 'opacity-0'}`}>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-6 gap-3 sm:gap-4">
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Todos los Módulos</h2>
                  <p className="text-sm sm:text-base text-gray-600">
                    {modules.length} módulos disponibles
                  </p>
                </div>
              </div>
              
              <div className="grid gap-3 sm:gap-4 md:gap-6 grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                {modules.map((module, index) => (
                  <div
                    key={module.id}
                    ref={el => moduleRefs.current[module.id] = el}
                    className={`group bg-white rounded-lg shadow-sm border p-3 sm:p-4 md:p-6 cursor-pointer hover:shadow-xl transition-all duration-500 hover:scale-[1.02] md:hover:scale-105 hover:-translate-y-1 lazy-animate ${
                      animationReady ? 'animate-scaleIn' : 'opacity-0'
                    }`}
                    style={{ animationDelay: `${index * 0.1}s` }}
                    onClick={() => handleModuleClick(module)}
                  >
                    <div className="flex items-start justify-between mb-3 gap-2">
                      <div className="flex items-center space-x-2 min-w-0 flex-1">
                        <div className="bg-gray-100 p-2 sm:p-2.5 md:p-3 rounded-lg md:rounded-xl group-hover:bg-blue-100 transition-colors flex-shrink-0">
                          <span className="text-lg sm:text-xl md:text-2xl group-hover:scale-110 transition-transform inline-block">{module.icon}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm sm:text-base md:text-lg font-bold text-gray-900 group-hover:text-blue-900 transition-colors line-clamp-2">
                            {module.title}
                          </h3>
                          <div className="flex items-center space-x-1 mt-1">
                            {module.progress_percentage === 100 ? (
                              <>
                                <CheckCircle className="w-3 h-3 text-green-600 flex-shrink-0" />
                                <span className="text-xs text-green-600 font-medium truncate">Completado</span>
                              </>
                            ) : module.progress_percentage > 0 ? (
                              <>
                                <div className="w-3 h-3 rounded-full border-2 border-blue-600 flex items-center justify-center flex-shrink-0">
                                  <div className="w-1 h-1 bg-blue-600 rounded-full" />
                                </div>
                                <span className="text-xs text-blue-600 font-medium truncate">En progreso</span>
                              </>
                            ) : (
                              <>
                                <Play className="w-3 h-3 text-gray-400 flex-shrink-0" />
                                <span className="text-xs text-gray-500 font-medium truncate">Disponible</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      {module.progress_percentage > 0 && (
                        <div className={`px-1.5 py-0.5 sm:px-2 sm:py-1 rounded-full text-xs font-bold flex-shrink-0 ${
                          module.progress_percentage === 100 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-blue-100 text-blue-800'
                        }`}>
                          {module.progress_percentage}%
                        </div>
                      )}
                    </div>
                    
                    <p className="text-gray-600 mb-3 text-xs sm:text-sm leading-relaxed line-clamp-2">
                      {module.description}
                    </p>
                    
                    <div className="space-y-2 sm:space-y-3">
                      <div className="flex items-center justify-between text-xs sm:text-sm">
                        <span className="text-gray-600">Progreso</span>
                        <span className="font-medium text-gray-900">
                          {module.progress_percentage}%
                        </span>
                      </div>
                      
                      <div className="w-full h-1.5 sm:h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all duration-1000 ${
                            module.progress_percentage === 100
                              ? 'bg-green-500'
                              : module.progress_percentage > 0
                              ? 'bg-blue-500'
                              : 'bg-gray-300'
                          }`}
                          style={{ width: `${module.progress_percentage}%` }}
                        />
                      </div>
                      
                      <div className="flex items-center justify-between text-xs text-gray-500">
                        <div className="flex items-center space-x-1">
                          <BookOpen className="w-3 h-3 flex-shrink-0" />
                          <span className="truncate">{module.total_chapters || 0} cap.</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Clock className="w-3 h-3 flex-shrink-0" />
                          <span className="truncate">{module.total_duration || '0min'}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="mt-3 flex justify-between items-center gap-2">
                      <span className={`px-2 py-1 text-xs rounded-full font-medium truncate ${
                        module.progress_percentage === 100
                          ? 'bg-green-100 text-green-800'
                          : module.progress_percentage > 0
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {module.progress_percentage === 100
                          ? 'Completado'
                          : module.progress_percentage > 0
                          ? 'En progreso'
                          : 'No iniciado'
                        }
                      </span>
                      
                      <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-blue-600 group-hover:translate-x-1 transition-all flex-shrink-0" />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className={`bg-blue-50 border border-blue-200 rounded-lg p-4 sm:p-6 mb-6 lazy-animate ${animationReady ? 'animate-fadeIn stagger-4' : 'opacity-0'}`}>
              <div className="flex items-start space-x-3">
                <div className="bg-blue-100 p-2 rounded-lg flex-shrink-0">
                  <Target className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="text-base sm:text-lg font-semibold text-blue-900 mb-2">
                    Tu Ruta de Aprendizaje
                  </h3>
                  <p className="text-blue-800 mb-3 text-sm sm:text-base leading-relaxed">
                    Explora todos los módulos disponibles y comienza tu viaje de aprendizaje.
                  </p>
                  <div className="grid grid-cols-2 sm:flex sm:flex-wrap items-center gap-3 sm:gap-4 text-xs sm:text-sm">
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-green-600 flex-shrink-0" />
                      <span className="text-blue-800">Completado</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 sm:w-4 sm:h-4 rounded-full border-2 border-blue-600 flex items-center justify-center flex-shrink-0">
                        <div className="w-1 h-1 sm:w-1.5 sm:h-1.5 bg-blue-600 rounded-full" />
                      </div>
                      <span className="text-blue-800">En progreso</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Play className="w-3 h-3 sm:w-4 sm:h-4 text-gray-500 flex-shrink-0" />
                      <span className="text-blue-800">Disponible</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {userStats.completedChapters === 0 && (
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-4 sm:p-6 mt-6 sm:mt-8 animate-fadeIn">
                <div className="text-center">
                  <div className="bg-blue-100 w-12 h-12 sm:w-16 sm:h-16 rounded-full flex items-center justify-center mx-auto mb-4 transition-transform hover:scale-110 animate-bounce-gentle">
                    <BookOpen className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600" />
                  </div>
                  <h3 className="text-lg sm:text-2xl font-bold text-blue-900 mb-2">
                    ¡Bienvenido a Universidad de Asesores!
                  </h3>
                  <p className="text-blue-800 mb-4 text-sm sm:text-base leading-relaxed">
                    Estás a punto de comenzar tu viaje de aprendizaje. Selecciona un módulo para empezar.
                  </p>
                  {getNextModule && (
                    <button
                      onClick={() => handleModuleClick(getNextModule)}
                      className="inline-flex items-center space-x-2 px-4 sm:px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all duration-300 text-sm sm:text-base hover:scale-105 transform hover:shadow-lg"
                    >
                      <Play className="w-4 h-4" />
                      <span>Comenzar con {getNextModule.title}</span>
                    </button>
                  )}
                </div>
              </div>
            )}

            {userStats.completedModules > 0 && userStats.completedModules === userStats.totalModules && (
              <div className="bg-gradient-to-r from-green-50 to-yellow-50 border border-green-200 rounded-lg p-4 sm:p-6 mt-6 sm:mt-8 animate-fadeIn">
                <div className="text-center">
                  <div className="bg-yellow-100 w-12 h-12 sm:w-16 sm:h-16 rounded-full flex items-center justify-center mx-auto mb-4 transition-transform hover:scale-110 animate-bounce-gentle">
                    <Award className="w-6 h-6 sm:w-8 sm:h-8 text-yellow-600" />
                  </div>
                  <h3 className="text-lg sm:text-2xl font-bold text-green-900 mb-2">
                    ¡Felicitaciones!
                  </h3>
                  <p className="text-green-800 mb-4 text-sm sm:text-base leading-relaxed">
                    Has completado todos los módulos disponibles con un tiempo total de estudio de{' '}
                    <span className="font-bold">{formatWatchTime(userStats.totalWatchTime)}</span>. 
                    ¡Excelente trabajo en tu desarrollo profesional!
                  </p>
                  <p className="text-xs sm:text-sm text-green-700">
                    Continúa revisando el contenido para reforzar tu aprendizaje y mantente atento a nuevos módulos.
                  </p>
                </div>
              </div>
            )}
          </main>
        </div>
      )}
    </ModulesErrorBoundary>
  );
};

export default Dashboard;